/**
 * LanguageSection Component
 * Select preferred language
 */

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useUpdateUserSettings } from "../hooks/useSettings"
import { getLanguages } from "../utils/language.utils"

export function LanguageSection() {
  const { t, i18n } = useTranslation(["settings", "common"])
  const user = useAuthStore(state => state.user)
  const { mutate: updateSettings, isPending } = useUpdateUserSettings()

  const currentLanguage = user?.userSettings?.languagePreferences || "en"
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage)

  const hasUnsavedChanges = selectedLanguage !== currentLanguage

  const handleSave = () => {
    // Immediately change the language in i18n for instant feedback
    i18n.changeLanguage(selectedLanguage)

    updateSettings({
      languagePreferences: selectedLanguage as "en" | "es",
    })
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Section Header */}
      <div>
        <h1 className="text-2xl font-bold text-charcoal uppercase section-heading">
          {t("language.title")}
        </h1>
        <p className="text-sm text-stone mt-1">{t("language.description")}</p>
      </div>

      <div className="flex flex-col gap-6 max-w-md">
        <div className="space-y-2">
          <Label className="text-xs uppercase text-stone">{t("language.title")} *</Label>
          <Select
            value={selectedLanguage}
            onValueChange={value => setSelectedLanguage(value as "en" | "es")}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("language.selectPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {getLanguages().map(lang => (
                <SelectItem key={lang.value} value={lang.value}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={!hasUnsavedChanges}
          isLoading={isPending}
          className="px-20 w-fit"
        >
          {t("buttons.save", { ns: "common" })}
        </Button>
      </div>
    </div>
  )
}
